import React, { useEffect } from "react";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import PieQuickStats from "src/features/attendance/components/AttendanceDashboard/AttendancePie";
import RiskMeter from "src/features/attendance/components/AttendanceDashboard/RiskMeter";
import AttendanceHeatmap from "src/features/attendance/components/AttendanceDashboard/AttendanceHeatmap";
import AttendanceOverTime from "src/features/attendance/components/AttendanceDashboard/AttendanceOverTime";
import MessagesCount from "src/features/attendance/components/AttendanceDashboard/MessagesCount";
import {
  useGroupsListGet,
  useListAbsencesGet,
  useStudentsListGet,
} from "src/app/services/apiHooks";
import { useToken } from "src/app/hooks";
import ContentLoading from "src/app/components/ContentLoading";
import { GroupFilter } from "src/features/attendance/components/AttendanceDashboard/utils";
import {
  get29DaysAgo,
  get29DaysAhead,
  getCurrentTime,
  getEndOfToday,
  getStartOfToday,
  getTomorrow,
} from "src/app/utils/timeUtils";

export const TWENTY_NINE_DAYS_AGO = get29DaysAgo();
export const TWENTY_NINE_DAYS_AHEAD = get29DaysAhead();
export const CURRENT_TIME = getCurrentTime();
export const TOMORROW = getTomorrow();
export const START_OF_TODAY = getStartOfToday();
export const END_OF_TODAY = getEndOfToday();

const AttendanceDashboard: React.FC = () => {
  const token = useToken();
  const [groupFilter, setGroupFilter] = React.useState<GroupFilter>("all");
  const { isLoading: isLoadingAbsences } = useListAbsencesGet({
    token,
    since: TWENTY_NINE_DAYS_AGO,
    to: TWENTY_NINE_DAYS_AHEAD,
  });
  const { isLoading: isLoadingStudents } = useStudentsListGet(
    {
      token,
    },
    {
      selectFromResult: (response) => ({
        numOfStudents: response?.data?.length,
        ...response,
      }),
    }
  );
  const { data: groups } = useGroupsListGet({ token });

  // If the selected group is removed (deleted/renamed), fall back to "all".
  useEffect(() => {
    if (
      typeof groupFilter === "number" &&
      groups &&
      !groups.some((group) => group.serial === groupFilter)
    ) {
      setGroupFilter("all");
    }
  }, [groups, groupFilter]);

  if (isLoadingAbsences || isLoadingStudents) {
    return <ContentLoading />;
  }

  // The dashboard's "quota" is the allowed number of absentees. Per group that
  // is the group's `cap`; when it's not set (no limit) or no group is picked we
  // fall back to the global quota the cards already fetch.
  const selectedGroupCap =
    typeof groupFilter === "number"
      ? groups?.find((group) => group.serial === groupFilter)?.cap ?? null
      : null;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControl size="small" sx={{ width: { xs: "100%", sm: 260 } }}>
          <InputLabel>סינון לפי קבוצה</InputLabel>
          <Select
            value={groupFilter}
            label="סינון לפי קבוצה"
            onChange={(event) =>
              setGroupFilter(event.target.value as GroupFilter)
            }
          >
            <MenuItem value="all">כל הקבוצות</MenuItem>
            {groups?.map((group) => (
              <MenuItem key={group.serial} value={group.serial}>
                {group.nickname}
              </MenuItem>
            ))}
            <MenuItem value="none">
              <em>ללא קבוצה</em>
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <RiskMeter groupFilter={groupFilter} groupCap={selectedGroupCap} />
        <MessagesCount groupFilter={groupFilter} />
      </Grid>
      <Grid item xs={12} md={6}>
        <PieQuickStats groupFilter={groupFilter} />
      </Grid>
      <Grid item xs={12}>
        <AttendanceOverTime groupFilter={groupFilter} groupCap={selectedGroupCap} />
      </Grid>
      <Grid item xs={12}>
        <AttendanceHeatmap groupFilter={groupFilter} groupCap={selectedGroupCap} />
      </Grid>
    </Grid>
  );
};

export default AttendanceDashboard;
